import type { Equipment } from "@tiny-seas/shared";
import type { CharacterAction, CharacterPose } from "./character-pose.ts";

export const CHARACTER_TIMING = {
  equipSeconds: 0.18, stanceSeconds: 0.18, actionSeconds: 0.12, attackReleaseSeconds: 0.08, handReleaseSeconds: 0.08,
  airDelay: 0.1, airIn: 0.15, airOut: 0.1, landMinAir: 0.25, landSeconds: 0.18,
} as const;

export type CharacterStance = "stand" | "crouch" | "sprint" | "slide";
export type CharacterAirborne = "grounded" | "rising" | "falling";

export interface CharacterState {
  /** The transition key changed this update. */
  changed: boolean;
  /** 0 … 1 linear progress of the current pose transition. */
  transition: number;
  /** Hands open briefly after an action, station or equipment change. */
  release: boolean;
  /** 0 … 1 progress of the equipment swap. */
  equipmentBlend: number;
  shownEquipment: Equipment;
  toolsVisible: boolean;
  stance: CharacterStance;
  airborne: CharacterAirborne;
  /** 0 … 1 blend into the airborne pose. */
  airBlend: number;
  /** 0 … 1 landing progress, or -1 when not landing. */
  landing: number;
  /** 0.2 … 1 landing strength from the touchdown speed. */
  landingImpact: number;
}

/** Action/stance/airborne state machine and transition timing; Babylon-free and allocation-free per frame. */
export class CharacterAnimator {
  readonly state: CharacterState = {
    changed: false, transition: 1, release: false, equipmentBlend: 1, shownEquipment: "shovel", toolsVisible: true,
    stance: "stand", airborne: "grounded", airBlend: 0, landing: -1, landingImpact: 0,
  };
  private initialized = false;
  private action: CharacterAction = "idle";
  private station = "";
  private equipment: Equipment = "shovel";
  private attacking = false;
  private blocking = false;
  private reloading = false;
  private duration: number = CHARACTER_TIMING.actionSeconds;
  private releaseRemaining = 0;
  private airTime = 0;
  private lastVy = 0;

  reset(): void { this.initialized = false; }

  update(dt: number, pose: Readonly<CharacterPose>): CharacterState {
    const s = this.state, T = CHARACTER_TIMING;
    const stance: CharacterStance = pose.action === "launch" ? "stand"
      : pose.slideProgress >= 0 ? "slide" : pose.sprinting ? "sprint" : pose.crouched ? "crouch" : "stand";
    const attacking = pose.attackPhase >= 0, reloading = pose.pistolReloadProgress >= 0;
    if (!this.initialized) {
      s.changed = false; s.transition = 1; s.equipmentBlend = 1; s.release = false;
      s.shownEquipment = pose.equipment; s.airborne = "grounded"; s.airBlend = 0; s.landing = -1; s.landingImpact = 0;
      this.releaseRemaining = 0; this.airTime = 0; this.lastVy = 0;
    } else {
      const equipmentChanged = pose.equipment !== this.equipment;
      const stanceChanged = stance !== s.stance;
      const actionChanged = pose.action !== this.action || pose.station !== this.station;
      s.changed = equipmentChanged || stanceChanged || actionChanged || attacking !== this.attacking
        || pose.blocking !== this.blocking || reloading !== this.reloading;
      if (s.changed) {
        s.transition = 0;
        this.duration = equipmentChanged ? T.equipSeconds : stanceChanged ? T.stanceSeconds
          : this.attacking && !attacking ? T.attackReleaseSeconds : T.actionSeconds;
      } else s.transition = Math.min(1, s.transition + dt / this.duration);
      this.releaseRemaining = Math.max(0, this.releaseRemaining - dt);
      if (equipmentChanged || actionChanged) this.releaseRemaining = T.handReleaseSeconds;
      s.release = this.releaseRemaining > 0;
      if (equipmentChanged) s.equipmentBlend = 0;
      else s.equipmentBlend = Math.min(1, s.equipmentBlend + dt / T.equipSeconds);
      if (s.equipmentBlend >= 0.5) s.shownEquipment = pose.equipment;
    }
    s.stance = stance;
    s.toolsVisible = (pose.action === "idle" || pose.action === "dig" || pose.action === "fill" || pose.action === "throw" || pose.action === "anchorKick") && pose.slideProgress < 0 && pose.station === "";

    if (this.initialized) {
      const free = (pose.action === "idle" || pose.action === "dig" || pose.action === "chest") && pose.slideProgress < 0;
      // A touchdown reports landing 0 this update; progress starts on the next one.
      if (s.landing >= 0) {
        s.landing += dt / T.landSeconds;
        if (s.landing >= 1) s.landing = -1;
      }
      if (!free || pose.grounded) {
        if (s.airborne !== "grounded" && free && this.airTime >= T.landMinAir) {
          s.landing = 0;
          s.landingImpact = Math.max(0.2, Math.min(1, -this.lastVy / 10));
        }
        s.airborne = "grounded"; this.airTime = 0;
      } else {
        this.airTime += dt; this.lastVy = pose.verticalSpeed;
        s.airborne = pose.verticalSpeed > 0 ? "rising" : "falling";
      }
      if (s.airborne !== "grounded" && this.airTime >= T.airDelay) s.airBlend = Math.min(1, s.airBlend + dt / T.airIn);
      else s.airBlend = Math.max(0, s.airBlend - dt / T.airOut);
    }

    this.action = pose.action; this.station = pose.station; this.equipment = pose.equipment;
    this.attacking = attacking; this.blocking = pose.blocking; this.reloading = reloading;
    this.initialized = true;
    return s;
  }
}
